'use client';

// Orchestrates the "approve trading" flow for Polymarket. Reads current
// state directly from Polygon RPC, kicks off USDC + CTF approvals if
// needed, polls until confirmed, then re-reads state. Single entry point
// so the profile page can present a "Approve trading" button without
// owning the multi-step machinery.

import type { Address, Hex } from 'viem';
import { withPolymarketAccount } from '../vault/index.js';
import {
  approveCtf,
  approveUsdc,
  readApprovalState,
  readUsdcBalance,
  waitForReceipt,
  type ApprovalState,
} from './polygon.js';
import { useStore } from '../store.js';

// Threshold for what we count as "approved" — Polymarket's UI uses a
// large allowance + the contract supports MAX_UINT256, so we treat any
// allowance >= 1 USDC (1e6 units) as "good enough" for sanity-check.
const APPROVED_THRESHOLD = 1_000_000n;

export interface ApprovalView {
  state: ApprovalState | null;
  balanceUsdc: bigint | null;
  loading: boolean;
  step: 'idle' | 'reading' | 'approving-usdc' | 'approving-ctf' | 'confirming';
  lastTxHash: Hex | null;
  reason: string | null;
}

type Listener = (v: ApprovalView) => void;
const listeners = new Set<Listener>();
let view: ApprovalView = {
  state: null,
  balanceUsdc: null,
  loading: false,
  step: 'idle',
  lastTxHash: null,
  reason: null,
};

function emit(): void {
  for (const l of listeners) l(view);
}

function patch(p: Partial<ApprovalView>): void {
  view = { ...view, ...p };
  emit();
}

export function subscribeApprovals(fn: Listener): () => void {
  listeners.add(fn);
  fn(view);
  return () => {
    listeners.delete(fn);
  };
}

export function getApprovalView(): ApprovalView {
  return view;
}

export async function refreshApprovalState(): Promise<void> {
  const address = useStore.getState().vault.polymarketAddress as Address | null;
  if (!address) {
    patch({ state: null, balanceUsdc: null, reason: 'polymarket key missing' });
    return;
  }
  patch({ loading: true, step: 'reading', reason: null });
  try {
    const [state, balance] = await Promise.all([
      readApprovalState(address),
      readUsdcBalance(address),
    ]);
    patch({ state, balanceUsdc: balance, loading: false, step: 'idle' });
  } catch (err) {
    patch({ loading: false, step: 'idle', reason: String(err) });
  }
}

export function isFullyApproved(s: ApprovalState | null): boolean {
  return !!s && s.usdcAllowance >= APPROVED_THRESHOLD && s.ctfApproved;
}

export async function runApprovals(): Promise<{ ok: boolean; reason?: string }> {
  const address = useStore.getState().vault.polymarketAddress as Address | null;
  if (!address) return { ok: false, reason: 'polymarket key missing' };
  patch({ loading: true, step: 'reading', reason: null, lastTxHash: null });
  let state: ApprovalState;
  try {
    state = await readApprovalState(address);
    patch({ state });
  } catch (err) {
    patch({ loading: false, step: 'idle', reason: `state read: ${String(err)}` });
    return { ok: false, reason: String(err) };
  }
  // USDC first — most users have it; CTF is a single-byte storage write.
  if (state.usdcAllowance < APPROVED_THRESHOLD) {
    patch({ step: 'approving-usdc' });
    const result = await runOne((account) => approveUsdc(account));
    if (!result.ok) {
      patch({ loading: false, step: 'idle', reason: result.reason ?? 'usdc approve failed' });
      return result;
    }
  }
  if (!state.ctfApproved) {
    patch({ step: 'approving-ctf' });
    const result = await runOne((account) => approveCtf(account));
    if (!result.ok) {
      patch({ loading: false, step: 'idle', reason: result.reason ?? 'ctf approve failed' });
      return result;
    }
  }
  // Final read so the UI shows the persisted state.
  await refreshApprovalState();
  patch({ loading: false, step: 'idle' });
  return { ok: true };
}

async function runOne(
  sendFn: (account: import('viem').Account) => Promise<Hex>,
): Promise<{ ok: boolean; reason?: string }> {
  try {
    const hash = await withPolymarketAccount(sendFn);
    if (!hash) return { ok: false, reason: 'vault unlocked but polymarket key missing' };
    patch({ lastTxHash: hash, step: 'confirming' });
    await waitForReceipt(hash);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: String(err) };
  }
}
